import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { PageHeader, SectionCard } from '../../components/ui';
import { useAuthStore, UserRole } from '../../store/authStore';
import { tokens } from '../../theme';

interface SettingItem {
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  /** Te same role co w trasach w App.tsx — pozycja pokazuje sie tylko tym, ktorzy moga ja otworzyc. */
  roles: UserRole[];
}

interface SettingSection {
  label: string;
  description: string;
  items: SettingItem[];
}

const SETTING_SECTIONS: SettingSection[] = [
  {
    label: 'Salon',
    description: 'Dane firmy i to, co widzą klienci',
    items: [
      { title: 'Profil biznesu', description: 'Nazwa, adres, logo, godziny otwarcia i metody płatności', icon: <StorefrontOutlinedIcon />, path: '/salon/profile', roles: ['SALON_OWNER', 'IT'] },
      { title: 'Wizytówka WWW', description: 'Publiczna strona salonu, link do rezerwacji, publikacja', icon: <PublicOutlinedIcon />, path: '/salon/website', roles: ['SALON_OWNER', 'IT'] },
      { title: 'Płatności', description: 'Przyjmowanie płatności online od klientów przez Stripe', icon: <PaymentOutlinedIcon />, path: '/salon/payments', roles: ['SALON_OWNER'] },
    ],
  },
  {
    label: 'Zespół i dostęp',
    description: 'Kto ma dostęp do salonu i w jakim zakresie',
    items: [
      { title: 'Użytkownicy i role', description: 'Zaproszenia, role i dostęp do lokalizacji', icon: <GroupOutlinedIcon />, path: '/salon/users', roles: ['SALON_OWNER', 'SUPER_ADMIN'] },
    ],
  },
  {
    label: 'Konto',
    description: 'Twoje dane logowania i plan Visitio',
    items: [
      { title: 'Moje konto', description: 'Imię, nazwisko, e-mail i zmiana hasła', icon: <PersonOutlineOutlinedIcon />, path: '/salon/account', roles: ['SALON_OWNER', 'STAFF', 'MANAGER', 'ACCOUNTANT', 'ANALYST', 'MARKETER', 'IT'] },
      { title: 'Subskrypcja', description: 'Plan Visitio, faktury i rozliczenia', icon: <WorkspacePremiumOutlinedIcon />, path: '/salon/subscription', roles: ['SALON_OWNER'] },
    ],
  },
  {
    label: 'Funkcje',
    description: 'Oferta salonu i stany magazynowe',
    items: [
      { title: 'Usługi i cennik', description: 'Usługi, ceny i czas trwania zabiegów', icon: <ContentCutOutlinedIcon />, path: '/salon/services', roles: ['SALON_OWNER', 'STAFF'] },
      { title: 'Magazyn', description: 'Produkty, dostawy i inwentaryzacja', icon: <Inventory2OutlinedIcon />, path: '/salon/inventory', roles: ['SALON_OWNER'] },
    ],
  },
];

const SettingRow: React.FC<{ item: SettingItem; first: boolean }> = ({ item, first }) => (
  <ButtonBase
    component={RouterLink}
    to={item.path}
    sx={{
      display: 'flex',
      width: '100%',
      textAlign: 'left',
      alignItems: 'center',
      gap: 1.75,
      px: { xs: 2, md: 2.5 },
      py: 1.75,
      borderTop: first ? 'none' : `1px solid ${tokens.line}`,
      transition: 'background-color .15s ease',
      '&:hover': { bgcolor: tokens.plumSofter },
      '&:hover .setting-chevron': { color: tokens.plum },
      '&.Mui-focusVisible': { outlineOffset: -2 },
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '11px',
        bgcolor: tokens.plumSoft,
        color: tokens.plum,
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
        '& .MuiSvgIcon-root': { fontSize: 21 },
      }}
    >
      {item.icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', color: tokens.ink }}>{item.title}</Typography>
      <Typography sx={{ fontSize: '0.875rem', color: tokens.muted, lineHeight: 1.45 }}>{item.description}</Typography>
    </Box>
    <ChevronRightRoundedIcon className="setting-chevron" sx={{ color: tokens.subtle, flexShrink: 0, transition: 'color .15s ease' }} />
  </ButtonBase>
);

export const SalonSettingsPage: React.FC = () => {
  const { user } = useAuthStore();
  const role = user?.role;

  const sections = SETTING_SECTIONS
    .map((section) => ({ ...section, items: section.items.filter((item) => !!role && item.roles.includes(role)) }))
    .filter((section) => section.items.length > 0);

  return (
    <Box>
      <PageHeader
        breadcrumbs={[{ label: 'Ustawienia salonu' }]}
        title="Ustawienia"
        description="Wszystkie ustawienia salonu i Twojego konta w jednym miejscu."
      />

      <Box
        sx={{
          display: 'grid',
          gap: 2.5,
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' },
          alignItems: 'start',
        }}
      >
        {sections.map((section) => (
          <SectionCard key={section.label} title={section.label} subtitle={section.description} padding="none">
            <Stack>
              {section.items.map((item, i) => (
                <SettingRow key={item.path} item={item} first={i === 0} />
              ))}
            </Stack>
          </SectionCard>
        ))}
      </Box>
    </Box>
  );
};

export default SalonSettingsPage;
